import { useState, useEffect} from "react";
import IconButton from '@mui/material/IconButton';
import { Button } from '@mui/material';
import {Divider} from "@mui/material";
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AuthService from "services/AuthService";
import { CircularProgress } from "@mui/material";
import { CircularProgressWithLabel } from "components";
import { getCachedData } from "api/Cache";
import EventsEmitter from "events/EventsEmitter";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import AlertNotification from "components/AlertNotification";
import {useDispatch, useSelector} from 'react-redux';
import { RootState } from 'store';
import { refreshProjectsList } from 'store/main/userManagement';
import jwtService from 'services/jwtService';
import { trackEvent } from "./Tracking";


type IUserProject = {
    name: string;
    id : string;
    access:string;
    project:string;
    userid:number
};

type IProject = {
    name: string;
    id : string;
};

type IMember = {
    member: string,
    project : string,
    accessLevel : string,
}


const UserProjectsSelected = (props:any) => {
    
    const dispatch = useDispatch();
    const data = getCachedData();
    const {id, email} = props;

    let selectedProjectsList = useSelector((state:RootState) => state.main.addMember.selectedProjectsList)

    const [currentId, setCurrentId] = useState('');
    const [currentAccess, setCurrentAccess] = useState('');
    const [accessLevelError, setAccessLevelError] = useState(false);
    const [helperTextAccess, setHelperTextAccess] = useState("");
    const [deleteId, setDeleteId] = useState('')
    const [deleteIndex, setDeleteIndex] = useState('')
    const [showConfirm, setShowConfirm] = useState(false)
    const [currentRef, setCurrentRef] = useState('')
    const [saveLoading, setSaveLoading] = useState(false)
    const [allProjects, setAllProjects] = useState<IProject[]>([])
    const [loadingProjects, setLoadingProjects] = useState(false)
    const [message, setMessage] = useState('')
    const [showSavedNotification, setShowSavedNotification] = useState(false)
    
    
    const [progress, setProgress] = useState(0);
    const [allUserProjects, setAllUserProjects] = useState<IUserProject[]>([])
    
    const refreshData = async (id:any, allList:any) => {
        setLoadingProjects(true)
        let list:any = [];
        const endpoint = process.env.REACT_APP_BASE_URL + '/user/projects';
        
        try{
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {
                    userid : id, key_id : process.env.REACT_APP_BASE_APIKEY
                }
            });

            if(response.data) {
                let result:any = [];
                allList.forEach((item:any) => {
                    result[item['id']] = item['name']
                })
            
                response.data.forEach((item:any) => {
                    list.push({id: item.id, userid:item.userid, project:item.project, access:item.access, name: result[item.project]});
                })
                setLoadingProjects(false)
            }
           
            setAllUserProjects(list.filter((item:any) => item.access !== 'none'));
            dispatch(refreshProjectsList(list.filter((item:any) => item.access !== 'none')));
        }catch(error){
            console.error(error);
            setLoadingProjects(false)
        }           
    }

    useEffect(() => {
        const handleProgress = (curProgress: number) => {
            setProgress(curProgress);
        };
        EventsEmitter.on("projects_progress", handleProgress);
        return () => {
            EventsEmitter.removeEventListener("projects_progress", handleProgress);
        };
      
    }, [data]);

    useEffect(() => {
        const projectsList = data["projects"];
        let projectTab: IProject[] = [];
        const keys = Object.keys(projectsList);
        keys.forEach((key, index)  => {
            projectTab.push({name:projectsList[key]['name'], id:projectsList[key]['ID']})
            if(index === keys.length - 1){
                setAllProjects(projectTab)
                refreshData(id, projectTab)
            }
        }) 
    }, [data, id]);


    if (!data.loaded) {
        return progress === 0 ? <CircularProgress /> : <CircularProgressWithLabel value={progress} />;
    }

 
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
          backgroundColor: '#3dabe6',
          color: theme.palette.common.white,
        },
        [`&.${tableCellClasses.body}`]: {
          fontSize: 14,
          backgroundColor: '#fff',
        },
    }));
      
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
          backgroundColor: theme.palette.action.hover,
        },
        // hide last border
        '&:last-child td, &:last-child th': {
          border: 0,
        },
    }));

    

    const handleAccessLevel = (accessLevel:string, pid:any, index:any) => {

        const project = selectedProjectsList[index]['project']
        const name = selectedProjectsList[index]['name']
        let newProjectsList = [...selectedProjectsList];
        newProjectsList[index] = {"id" : pid, "userid" : id, "project" : project, "access" : accessLevel, "name" : name};
        dispatch(refreshProjectsList(newProjectsList));

        setCurrentAccess(accessLevel)
        setCurrentId(id)
        //setAccessLevel(accessLevel)
        if(accessLevel === ''){
            setAccessLevelError(true);
            setHelperTextAccess("Please select an access level");
        }else{
            setAccessLevelError(false);
            setHelperTextAccess("");
        }
    }

    const confirmDeleteRequest = () => {
        const project = selectedProjectsList[deleteIndex]['project']
        const name = selectedProjectsList[deleteIndex]['name']
        let newProjectsList = [...selectedProjectsList];
        newProjectsList[parseInt(deleteIndex)] = {"id" : deleteId, "userid" : id, "project" : project, "access" : "none", "name" : name};

        const elem:IMember = {
            member : email,
            project : project,
            accessLevel : "none"
        }
        remoteUserProject(elem)

        dispatch(refreshProjectsList(newProjectsList));

        deleteAccessLevel(deleteId)
        setShowConfirm(false);
    };

    const deleteAccessLevel  = async (id:any) =>{
        try{
            const endpoint = process.env.REACT_APP_BASE_URL + '/customer/deleteproject';
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {
                    id : id, key_id : process.env.REACT_APP_BASE_APIKEY
                }
            });

            if(response.data){
                setAllUserProjects(allUserProjects.filter((item:any) => item.id !== id))  
            }
            
        }catch(e){
            console.log(e)
        } 
    }

    const handleDelete = (id:any, index:any) =>{

        setDeleteIndex(index)
        setDeleteId(id)
        setShowConfirm(true);
    }

    const handleCloseDelete = () => {
        setShowConfirm(false)
    }


    const remoteUserProject = async (elem:IMember) => {
        
        try{
            const endpoint = process.env.REACT_APP_BASE_URL + '/vf/remove-user-project';
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {
                    email : elem.member, 
                    projectId : elem.project, 
                    key_id : process.env.REACT_APP_BASE_APIKEY
                }
            });

            if(response.data) {
                console.log(response.data + ' Delete project ' + elem.project + ' from user ' + elem.member + ' ...................')
            }
        }catch(e){
            console.log(e)
        }
    }

    

    const updateRemoteUserProject = async (elem:IMember) => {
        
        try{
            const endpoint = process.env.REACT_APP_BASE_URL + '/vf/update-project-level';
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {
                    email : elem.member, 
                    projectId : elem.project, 
                    access_level : elem.accessLevel,
                    key_id : process.env.REACT_APP_BASE_APIKEY
                }
            });

            if(response.data) {
                console.log(response.data + 'Assign project ' + elem.project + ' to user ' + elem.member + ' ...................')
            }
        }catch(e){
            console.log(e)
        }

        /*
        try{

            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: `/entity.ashx?id=${elem.project}:${btoa(elem.member)}&project=${elem.project}`,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {
                    access_level : elem.accessLevel, 
                    project : "users:" + elem.project, 
                    type : "USER_REF",
                    user_ref : btoa(elem.member)
                }
            });
            if(response.data.result === "success") {

                let response = await jwtService.fetchRequest({
                    method: 'POST',
                    url: `/entity.ashx?id=users:${elem.project}:${btoa(elem.member)}&project=users:${elem.project}`,
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    data: {
                        access_level : elem.accessLevel, 
                        project : "users:" + elem.project, 
                        type : "USER_REF",
                        user_ref : btoa(elem.member)
                    }
                });
                if(response.data.result === "success") {

                    let response = await jwtService.fetchRequest({
                        method: 'POST',
                        url: `/entity.ashx?id=${btoa(elem.member)}:${elem.project}&project=${btoa(elem.member)}`,
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        data: {
                            access_level : elem.accessLevel, 
                                project : btoa(elem.member), 
                                project_ref: elem.project,
                                type : "PROJECT_REF"
                        }
                    });
                    console.log("Add project to user : " + response)
                }
            }       
        }catch(e){
            console.log(e)
        }
        */
    }

    const updateUserAccessLevel = async (id:any, project:any, userid:any, index:any ) => {
        //setAccessLevel(userProjects[index]['access'])
        setSaveLoading(true)

        try{
            const endpoint = process.env.REACT_APP_BASE_URL + '/customer/addproject';
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {userid : userid, project: project, access : selectedProjectsList[index]['access'], mode : 'updated', key_id : process.env.REACT_APP_BASE_APIKEY}
            });

            if(response.data) {
                setMessage(response.data.msg)
                const elem:IMember = {
                    member : email,
                    project : project,
                    accessLevel : selectedProjectsList[index]['access']
                }
                updateRemoteUserProject(elem)
                setTimeout(() => {
                    setSaveLoading(false)
                    setShowSavedNotification(true)
                }, 500); 

                setTimeout(() => {
                    setShowSavedNotification(false)
                }, 5000);
            }
            setCurrentRef(id)

        }catch(e){
            console.log(e)
        }    
    }

    return (
      
        loadingProjects ? <CircularProgress />
        :
        allUserProjects.length > 0 ?
        <div style={{ margin: '10px 0' }}>
            <Dialog
                sx={{
                    '& .MuiPaper-root': {
                    backgroundColor: 'transparent', // Custom background color
                    },
                    '& .MuiDialog-paper': {
                        backgroundColor: '#fff !important',  // Change to your desired color
                    },
                }}
                open={showConfirm}
                onClose={handleCloseDelete}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">User deletion</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleCloseDelete}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                    >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <DialogContent id="alert-dialog-description">
                    Do you really want to remove user access to this project?
                    </DialogContent>
                </DialogContent>
                <DialogActions>
                    <Button onClick={confirmDeleteRequest} color="primary">
                    Yes
                    </Button>
                    <Button onClick={handleCloseDelete} color="primary">
                    No
                    </Button>
                </DialogActions>
            </Dialog>
            <Divider />
            {
                showSavedNotification ? 
                <AlertNotification variant={"standard"} type={"success"} message={message} />
                : ''
            }
            <TableContainer>    
                {
                selectedProjectsList.filter((item:any) => item.userid === id ).length > 0 ?                                   
                <Table aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Access</StyledTableCell>
                            <StyledTableCell></StyledTableCell>
                            <StyledTableCell></StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedProjectsList.filter((item:any) => item.userid === id ).map((row : IUserProject, index:any) => (
            
                        //{allUserProjects.map((row : IUserProject, index) => (
                        
                            <StyledTableRow key={row.id} style={{'display' : row.access === 'none' || !row.name ? 'none' : ''}}>
                                <StyledTableCell component="th" scope="row">
                                    {row.name}
                                </StyledTableCell>
                                <StyledTableCell component="th" scope="row">
                                    <TextField
                                        required
                                        fullWidth
                                        id={row.id}
                                        select
                                        label="Access level"
                                        value={ row.id === currentId ? currentAccess : row.access}
                                        sx={{marginTop:'8px'}}
                                        onChange={(e) => handleAccessLevel(e.target.value, row.id, index)}
                                    >
                                        <MenuItem value="read">Read-only</MenuItem>
                                        <MenuItem value="full">Full control</MenuItem>
                                    </TextField>
                                    
                                </StyledTableCell>
                                
                                <StyledTableCell component="th" scope="row">
                                    <Tooltip title="Delete" onClick={() => handleDelete(row.id, index)}>
                                        <IconButton>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </StyledTableCell>
                                
                                <StyledTableCell component="th" scope="row">
                                    
                                    <Button 
                                        onClick={() => {
                                            trackEvent("UserProfile", "My Team", "Update Project Access");
                                            updateUserAccessLevel(row.id, row.project, row.userid, index);
                                        }}
                                        variant="contained" 
                                        color="primary"
                                        style={{width: '90px'}}
                                    >
                                        {saveLoading && row.id === currentRef && <CircularProgress size={15} sx={{color:"#fff"}}/>} &nbsp;Save
                                    </Button>                                                      
                                        
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                            
                    </TableBody>
                </Table>
                :
                'No project found'
                }
            </TableContainer>
        </div> 
        : ''
    );
};

export default UserProjectsSelected;
